import React, { useMemo } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import useTranslation from '@/hooks/useTranslation';
import {
  clampDateToRange,
  formatIsoDate,
  getAbsenceDateLimits,
  getLowerBoundDate,
  parseDateValue,
} from '../utils/dateLimits';

const FilterControls = ({
  filters,
  onFilterChange,
  onClearFilters,
  requestCounts
}) => {
  const { t } = useTranslation(['timeOffManagement', 'common']);
  const translate = (key, fallback) => t(`timeOffManagement:${key}`, fallback);
  const hasActiveFilters = Boolean(
    (typeof filters?.search === 'string' && filters.search.trim())
      || filters?.startDate
      || filters?.endDate,
  );
  const { minDate, maxDate, minDateString, maxDateString } = useMemo(getAbsenceDateLimits, []);
  const endMinDateString = useMemo(
    () => formatIsoDate(getLowerBoundDate(filters?.startDate, minDate)),
    [filters?.startDate, minDate],
  );

  return (
    <div className="bg-card border border-border rounded-lg p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">
          {translate('filters.title', 'Filter Requests')}
        </h3>
        {hasActiveFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearFilters}
          >
            <Icon name="X" size={16} className="mr-2" />
            {translate('filters.clear', 'Clear Filters')}
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <Input
          label={translate('filters.labels.employee', 'Employee')}
          type="text"
          placeholder={translate('filters.searchPlaceholder', 'Search employee...')}
          value={filters?.search}
          onChange={(e) => onFilterChange('search', e?.target?.value)}
          className="w-full"
        />

        <DatePicker
          label={translate('filters.labels.startDate', 'Start Date')}
          value={filters?.startDate}
          onChange={(e) => {
            const boundedStart = clampDateToRange(e?.target?.value, minDate, maxDate);
            const currentEnd = clampDateToRange(filters?.endDate, minDate, maxDate);
            const startDateValue = parseDateValue(boundedStart);
            const endDateValue = parseDateValue(currentEnd);

            onFilterChange('startDate', boundedStart);

            if (
              boundedStart
              && currentEnd
              && startDateValue
              && endDateValue
              && endDateValue < startDateValue
            ) {
              onFilterChange('endDate', boundedStart);
            } else if (currentEnd !== filters?.endDate) {
              onFilterChange('endDate', currentEnd);
            }
          }}
          min={minDateString}
          max={maxDateString}
        />

        <DatePicker
          label={translate('filters.labels.endDate', 'End Date')}
          value={filters?.endDate}
          onChange={(e) => {
            const boundedEnd = clampDateToRange(e?.target?.value, minDate, maxDate);
            const boundedStart = clampDateToRange(filters?.startDate, minDate, maxDate);
            const startDateValue = parseDateValue(boundedStart);
            const endDateValue = parseDateValue(boundedEnd);

            if (
              boundedStart
              && boundedEnd
              && startDateValue
              && endDateValue
              && endDateValue < startDateValue
            ) {
              onFilterChange('endDate', boundedStart);
            } else {
              onFilterChange('endDate', boundedEnd);
            }
          }}
          min={endMinDateString}
          max={maxDateString}
        />
      </div>
      {/* Request Counts */}
      <div className="flex items-center space-x-6 pt-4 border-t border-border">
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-warning"></div>
          <span className="text-sm text-muted-foreground">
            {translate('filters.counts.pending', 'Pending')}:{' '}
            <span className="font-medium text-foreground">{requestCounts?.pending}</span>
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-success"></div>
          <span className="text-sm text-muted-foreground">
            {translate('filters.counts.approved', 'Approved')}:{' '}
            <span className="font-medium text-foreground">{requestCounts?.approved}</span>
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 rounded-full bg-error"></div>
          <span className="text-sm text-muted-foreground">
            {translate('filters.counts.denied', 'Denied')}:{' '}
            <span className="font-medium text-foreground">{requestCounts?.denied}</span>
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Icon name="Users" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground">
            {translate('filters.counts.total', 'Total')}:{' '}
            <span className="font-medium text-foreground">{requestCounts?.total}</span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default FilterControls;
